export const profileData = {
  name: "Arun Bharathwaj",
  title: "Platform Modernization Architect",
  tagline: "Cloud-native solutions design",
  subtitle: "Platform modernization | OpenShift architecture | Infrastructure automation",
  description: "I shape modern platforms and automation foundations that help organisations deliver change with confidence.",
  email: "aarunbharathwaj@gmail.com",
  phone: "+6594739239",
  phoneDisplay: "+65 9473 9239",
  whatsapp: "https://wa.me/6594739239?text=Hi%20Arun%2C%20I%20found%20your%20portfolio%20and%20would%20like%20to%20connect.",
  github: "https://github.com/secopsarch",
  linkedin: "https://www.linkedin.com/in/arun-bharathwaj",
  domain: "arunkube.org"
};

export const aboutData = {
  intro: "I am a Platform Modernization Architect focused on shaping cloud-native solutions that move organisations forward with clarity. My work brings together platform architecture, OpenShift and Kubernetes expertise, and infrastructure automation to make complex technology estates easier to evolve and operate.",
  quote: "My approach is architectural and pragmatic: create a clear path forward, make the secure path the easy path, and leave systems stronger than I found them.",
  approach: [
    "I translate technical complexity into decisions that stakeholders and delivery teams can act on.",
    "I bring structured problem-solving, clear communication and calm collaboration to platform change.",
    "I align architecture, delivery priorities and operational realities so that progress is sustainable."
  ],
  currentDirection: "I continue to deepen my practice across cloud-native solutions design, OpenShift architecture, Azure, Terraform, Ansible and infrastructure automation."
};

export const skillsData = [
  {
    id: 1,
    kicker: "ARCHITECTURE",
    title: "Hybrid cloud architecture",
    description: "Design connected cloud and on-premises foundations around workload needs, operational boundaries and a clear path to scale.",
    scope: ["Landing zones and connectivity", "Workload placement", "Cloud operating models"],
    icon: "cloud"
  },
  {
    id: 2,
    kicker: "MODERNIZE",
    title: "Platform modernization",
    description: "Turn complex estates into practical modernization roadmaps that teams can deliver without losing reliability or control.",
    scope: ["Estate assessment", "Target-state roadmaps", "Migration sequencing"],
    icon: "layers"
  },
  {
    id: 3,
    kicker: "PLATFORM",
    title: "OpenShift architecture",
    description: "Shape Kubernetes and OpenShift platforms for secure workloads, productive engineering teams and dependable day-two operations.",
    scope: ["Cluster and workload design", "Networking and storage", "Platform operations"],
    icon: "box"
  },
  {
    id: 4,
    kicker: "AUTOMATE",
    title: "Automation and GitOps",
    description: "Build repeatable infrastructure and delivery workflows that make change reviewable, testable and easier to recover.",
    scope: ["Terraform and Ansible", "CI/CD and GitOps", "Policy-driven delivery"],
    icon: "settings"
  },
  {
    id: 5,
    kicker: "GOVERN",
    title: "Security and compliance",
    description: "Embed identity, policy, observability and evidence into platform decisions so governance supports delivery from the start.",
    scope: ["Identity and access", "Security guardrails", "Compliance evidence"],
    icon: "shield"
  },
  {
    id: 6,
    kicker: "INTELLIGENCE",
    title: "AI-enabled operations",
    description: "Apply AI carefully to platform signals, knowledge and repetitive operations while keeping human review and clear guardrails.",
    scope: ["Operational intelligence", "Assisted investigation", "Responsible automation"],
    icon: "target"
  }
];

export const projectsData = [
  {
    id: 1,
    title: "Kubernetes platform foundations",
    summary: "A reference approach to cluster lifecycle, secure workloads and day-two operations.",
    tags: ["Kubernetes", "Platform Engineering"],
    description: "Explore a dependable baseline for teams running container workloads on Kubernetes: clear workload boundaries, repeatable environment setup and an operational model that supports day-two work.",
    focus: [
      "Cluster lifecycle and workload placement",
      "Configuration, secrets and service connectivity",
      "Observability and troubleshooting workflows",
      "Secure-by-default workload conventions"
    ],
    outcome: "A practical platform foundation that can be iterated on as application, security and operational needs evolve.",
    github: "https://github.com/secopsarch/kubernetes-platform",
    year: "2024"
  },
  {
    id: 2,
    title: "OpenShift operations playbook",
    summary: "Operational patterns for a supported, observable OpenShift platform.",
    tags: ["OpenShift", "Operations"],
    description: "Develop and document repeatable operating practices for an OpenShift environment, with attention to the components that have the greatest impact on workload reliability.",
    focus: [
      "Operator lifecycle and platform capabilities",
      "Storage, networking and monitoring dependencies",
      "Workload troubleshooting and capacity awareness",
      "Runbook-oriented operational documentation"
    ],
    outcome: "A structured foundation for operating the platform consistently while reducing time-to-diagnosis for common issues.",
    github: "https://github.com/secopsarch/openshift-ops",
    year: "2024"
  },
  {
    id: 3,
    title: "Terraform Azure foundations",
    summary: "Repeatable infrastructure patterns for Azure platform services.",
    tags: ["Terraform", "Azure", "IaC"],
    description: "Infrastructure-as-code modules and patterns for Azure platform services with focus on governance, networking and identity.",
    focus: [
      "Modular Terraform design for Azure resources",
      "Network architecture and connectivity patterns",
      "Identity and access management foundations",
      "Policy-driven governance automation"
    ],
    outcome: "Reusable infrastructure patterns that accelerate Azure adoption while maintaining security and compliance standards.",
    github: "https://github.com/secopsarch/terraform-azure",
    year: "2023"
  },
  {
    id: 4,
    title: "Automation delivery pipeline",
    summary: "CI/CD patterns for infrastructure and platform automation.",
    tags: ["Automation", "CI/CD", "GitOps"],
    description: "Structured delivery workflows that make infrastructure changes testable, reviewable and reliably deployable.",
    focus: [
      "GitOps workflows for infrastructure changes",
      "Testing strategies for infrastructure code",
      "Approval and compliance gates",
      "Rollback and recovery procedures"
    ],
    outcome: "Confidence in infrastructure changes through automated testing, clear review processes and reliable deployment patterns.",
    github: "https://github.com/secopsarch/automation-pipeline",
    year: "2023"
  }
];

export const techStackData = {
  cloud: ["Azure", "AWS", "Oracle Cloud (OCI)"],
  iac: ["Terraform", "Ansible", "Ansible AWX", "Automation Controller"],
  containers: ["Kubernetes", "OpenShift", "Docker", "AKS", "EKS", "Helm", "Kustomize"],
  cicd: ["Argo CD (GitOps)", "Azure DevOps", "GitLab CI/CD", "Jenkins", "Rundeck", "GitHub Actions"],
  security: ["ACS", "Microsoft Sentinel", "Defender", "Azure WAF", "CSPM", "Azure Arc", "PCI DSS", "MAS TRM", "CIS"],
  networking: ["F5 BIG-IP LTM/ASM", "Azure Firewall", "NSGs", "Route 53", "ALB / NLB", "NetworkPolicy", "VPN"],
  observability: ["Prometheus", "Grafana", "Zabbix", "Nagios", "Azure Monitor", "CloudWatch"],
  scripting: ["Python", "Bash", "Shell", "YAML"],
};

export const resumeData = {
  profile: "Platform Modernization Architect specialising in cloud-native solutions design, OpenShift architecture and infrastructure automation. I work where technical strategy, reliable platforms and well-managed delivery meet.",
  capabilities: [
    {
      title: "Platform modernization",
      description: "Architecture roadmaps for scalable, cloud-native operating models."
    },
    {
      title: "OpenShift & Kubernetes",
      description: "Platform design, cluster operations, networking, storage and troubleshooting."
    },
    {
      title: "Infrastructure automation",
      description: "Terraform, Ansible and repeatable operational workflows."
    },
    {
      title: "Project delivery",
      description: "Planning, stakeholder alignment and clear technical communication."
    }
  ],
  availability: "For platform modernization, cloud-native architecture or infrastructure automation collaboration, please get in touch."
};

export const blogPosts = [
  {
    id: 1,
    title: "Building resilient Kubernetes platforms",
    excerpt: "Key patterns for creating production-ready Kubernetes environments that teams trust.",
    date: "2024-08-01",
    tags: ["Kubernetes", "Platform Engineering"],
    readTime: "8 min read"
  },
  {
    id: 2,
    title: "OpenShift networking deep dive",
    excerpt: "Understanding OpenShift's network architecture and how to design for reliability.",
    date: "2024-07-15",
    tags: ["OpenShift", "Networking"],
    readTime: "12 min read"
  },
  {
    id: 3,
    title: "Infrastructure as Code best practices",
    excerpt: "Lessons learned from managing Terraform at scale across Azure environments.",
    date: "2024-06-28",
    tags: ["Terraform", "IaC"],
    readTime: "10 min read"
  }
];

export const certifications = [
  {
    id: 1,
    name: "Red Hat Certified Specialist in OpenShift Advanced Cluster Management",
    issuer: "Red Hat",
    status: "Active",
    expiry: "2029"
  },
  {
    id: 2,
    name: "Red Hat Certified OpenShift Administrator (EX280)",
    issuer: "Red Hat",
    status: "Active",
    expiry: "2029"
  },
  {
    id: 3,
    name: "Red Hat Certified Specialist in OpenShift Automation & Integration (EX380)",
    issuer: "Red Hat",
    status: "Active",
    expiry: "2029"
  },
  {
    id: 4,
    name: "HashiCorp Certified: Terraform Associate (003)",
    issuer: "HashiCorp",
    status: "Active",
    expiry: "2027"
  },
  {
    id: 5,
    name: "Microsoft Certified: Azure Administrator Associate",
    issuer: "Microsoft",
    status: "Active"
  },
  {
    id: 6,
    name: "Red Hat Certified Engineer (RHCE)",
    issuer: "Red Hat",
    status: "Active"
  },
  {
    id: 7,
    name: "Red Hat Certified Specialist in Ansible Automation",
    issuer: "Red Hat",
    status: "Active"
  },
  {
    id: 8,
    name: "Oracle Cloud Certified Architect Professional",
    issuer: "Oracle",
    status: "Active"
  },
  {
    id: 9,
    name: "ITIL 4 Foundation — IT Service Management",
    issuer: "AXELOS",
    status: "Active"
  },
  {
    id: 10,
    name: "Red Hat Certified Architect (RHCA)",
    issuer: "Red Hat",
    status: "In Progress"
  }
];
